/*
 * WS-клиент событий движка (§1.3). Браузер не шлёт Authorization на handshake →
 * доступ через одноразовый тикет: POST /ws/ticket (под токеном) → WS /ws?ticket=…
 * Первым приходит снимок состояния, далее — поток событий. Авто-переподключение
 * с backoff; 4401 (тикет невалиден) → пере-выпуск тикета; 401 на тикет → logout.
 */

import { ApiError, API_BASE, issueWsTicket } from './api'
import type { EngineState, WsEventMessage } from './types'

const WS_CLOSE_UNAUTHORIZED = 4401
/** Чистое закрытие нами (не реконнектим). */
const WS_CLOSE_GOING_AWAY = 1000

export type WsStatus = 'connecting' | 'open' | 'closed'

export interface EngineSocketHandlers {
  onSnapshot?: (state: EngineState) => void
  onEvent?: (event: WsEventMessage) => void
  onStatus?: (status: WsStatus) => void
  /** Токен отвергнут при выпуске тикета — требуется повторный вход. */
  onUnauthorized?: () => void
}

function buildWsUrl(ticket: string): string {
  const path = `${API_BASE}/ws`
  let base: URL
  if (/^https?:\/\//i.test(API_BASE)) {
    base = new URL(`${API_BASE}/ws`)
  } else {
    base = new URL(path, window.location.origin)
  }
  base.protocol = base.protocol === 'https:' ? 'wss:' : 'ws:'
  base.searchParams.set('ticket', ticket)
  return base.toString()
}

export class EngineSocket {
  private handlers: EngineSocketHandlers
  private ws: WebSocket | null = null
  private closedByUs = false
  private attempt = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null

  constructor(handlers: EngineSocketHandlers) {
    this.handlers = handlers
  }

  start(): void {
    this.closedByUs = false
    void this.connect()
  }

  stop(): void {
    this.closedByUs = true
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.ws) {
      // Снимаем хендлеры до close — иначе onclose запустит реконнект.
      this.ws.onopen = null
      this.ws.onmessage = null
      this.ws.onerror = null
      this.ws.onclose = null
      try {
        this.ws.close(WS_CLOSE_GOING_AWAY)
      } catch {
        /* уже закрыт */
      }
      this.ws = null
    }
    this.handlers.onStatus?.('closed')
  }

  private async connect(): Promise<void> {
    if (this.closedByUs) return
    this.handlers.onStatus?.('connecting')
    let ticket: string
    try {
      ticket = (await issueWsTicket()).ticket
    } catch (err) {
      // Этот сокет мог быть остановлен (новый login/logout), пока ждали тикет —
      // тогда не трогаем сессию (иначе устаревший сокет разлогинил бы свежую).
      if (this.closedByUs) return
      if (err instanceof ApiError && err.status === 401) {
        this.handlers.onUnauthorized?.()
        return
      }
      // 429 — лок-аут попыток на сервере (по умолчанию 5 мин): обычный backoff (≤15с)
      // молотил бы /ws/ticket весь лок-аут. Ждём дольше.
      this.scheduleReconnect(err instanceof ApiError && err.status === 429 ? 60_000 : undefined)
      return
    }
    if (this.closedByUs) return

    let ws: WebSocket
    try {
      ws = new WebSocket(buildWsUrl(ticket))
    } catch {
      this.scheduleReconnect()
      return
    }
    this.ws = ws

    ws.onopen = () => {
      this.attempt = 0
      this.handlers.onStatus?.('open')
    }

    ws.onmessage = (ev: MessageEvent<string>) => {
      let msg: unknown
      try {
        msg = JSON.parse(ev.data)
      } catch {
        return
      }
      if (!msg || typeof msg !== 'object') return
      const m = msg as { kind?: string; payload?: unknown }
      if (m.kind === 'snapshot') {
        this.handlers.onSnapshot?.(m.payload as EngineState)
      } else if (typeof m.kind === 'string') {
        this.handlers.onEvent?.(msg as WsEventMessage)
      }
    }

    ws.onerror = () => {
      /* onclose последует — реконнект там */
    }

    ws.onclose = (ev: CloseEvent) => {
      this.ws = null
      this.handlers.onStatus?.('closed')
      if (this.closedByUs) return
      // 4401 — тикет протух/невалиден: пере-выпускаем тикет и подключаемся снова
      // (не logout: токен мог быть валиден, истёк лишь короткоживущий тикет).
      void ev.code
      this.scheduleReconnect()
    }
  }

  private scheduleReconnect(minDelayMs?: number): void {
    if (this.closedByUs || this.reconnectTimer) return
    this.attempt += 1
    const backoff = Math.max(minDelayMs ?? 0, Math.min(1000 * 2 ** (this.attempt - 1), 15000))
    const jitter = backoff * 0.25 * Math.random()
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      void this.connect()
    }, backoff + jitter)
  }
}

export { WS_CLOSE_UNAUTHORIZED }
